import React, { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { useToast } from '@/hooks/use-toast';
import { Calendar, DollarSign, Upload } from 'lucide-react';
import { buildApiUrl } from '@/lib/config';

const CURRENCIES = [
  { code: 'USD', name: 'US Dollar', defaultRate: '1.0000' },
  { code: 'EUR', name: 'Euro', defaultRate: '0.8500' },
  { code: 'GBP', name: 'British Pound', defaultRate: '0.7300' },
  { code: 'CHF', name: 'Swiss Franc', defaultRate: '0.8800' },
  { code: 'AUD', name: 'Australian Dollar', defaultRate: '1.5000' },
  { code: 'CAD', name: 'Canadian Dollar', defaultRate: '1.3500' },
  { code: 'SGD', name: 'Singapore Dollar', defaultRate: '1.3200' },
  { code: 'JPY', name: 'Japanese Yen', defaultRate: '150.0000' },
  { code: 'THB', name: 'Thai Baht', defaultRate: '35.2500' },
  { code: 'AED', name: 'UAE Dirham', defaultRate: '3.6700' },
  { code: 'HKD', name: 'Hong Kong Dollar', defaultRate: '7.8200' },
  { code: 'IDR', name: 'Indonesian Rupiah', defaultRate: '15000.0000' },
  { code: 'MYR', name: 'Malaysian Ringgit', defaultRate: '4.6000' },
  { code: 'NZD', name: 'New Zealand Dollar', defaultRate: '1.6000' },
  { code: 'SAR', name: 'Saudi Arabian Riyal', defaultRate: '3.7500' },
  { code: 'VND', name: 'Vietnamese Dong', defaultRate: '24000.0000' },
  { code: 'CNY', name: 'Chinese Yuan', defaultRate: '7.2300' }
];

interface BulkForexRatesFormProps {
  onSuccess: () => void;
}

export default function BulkForexRatesForm({ onSuccess }: BulkForexRatesFormProps) {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split('T')[0]);
  const [rates, setRates] = useState<Record<string, string>>(() => {
    const initial: Record<string, string> = {};
    CURRENCIES.forEach(currency => {
      initial[currency.code] = currency.defaultRate;
    });
    return initial;
  });

  // Create bulk forex rates
  const createBulkRates = useMutation({
    mutationFn: async (data: { date: string; rates: { currencyCode: string; rate: string }[] }) => {
      const token = localStorage.getItem('authToken');
      const response = await fetch(buildApiUrl('api/forex-rates/bulk'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(data),
      });
      
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.message || 'Failed to create forex rates');
      }
      
      return response.json();
    },
    onSuccess: (result) => {
      toast({
        title: "Success",
        description: result.message || "Forex rates created successfully",
      });
      queryClient.invalidateQueries({ queryKey: ['/api/forex-rates'] });
      onSuccess();
    },
    onError: (error: Error) => {
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  const handleRateChange = (currencyCode: string, value: string) => {
    // Only allow positive numbers and empty string
    if (value === '' || (parseFloat(value) >= 0 && !isNaN(parseFloat(value)))) {
      setRates(prev => ({
        ...prev,
        [currencyCode]: value
      }));
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    // Filter out empty rates and validate positive values
    const validRates = Object.entries(rates)
      .filter(([_, rate]) => rate && parseFloat(rate) > 0)
      .map(([currencyCode, rate]) => ({ currencyCode, rate }));

    if (validRates.length === 0) {
      toast({
        title: "Error",
        description: "Please enter valid positive rates for at least one currency",
        variant: "destructive",
      });
      return;
    }

    createBulkRates.mutate({
      date: selectedDate,
      rates: validRates,
    });
  };

  const resetToDefaults = () => {
    const defaultRates: Record<string, string> = {};
    CURRENCIES.forEach(currency => {
      defaultRates[currency.code] = currency.defaultRate;
    });
    setRates(defaultRates);
  };

  return (
    <Card className="w-full max-w-4xl mx-auto">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Upload className="h-5 w-5" />
          Bulk Currency Rates Entry
        </CardTitle>
        <p className="text-sm text-gray-600">
          Enter exchange rates for multiple currencies at once. Only positive values are allowed.
        </p>
      </CardHeader>
      <CardContent>
        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Date Selection */}
          <div className="space-y-2">
            <Label htmlFor="date" className="flex items-center gap-2">
              <Calendar className="h-4 w-4" />
              Date
            </Label>
            <Input
              id="date"
              type="date"
              value={selectedDate}
              onChange={(e) => setSelectedDate(e.target.value)}
              className="max-w-xs"
              required
            />
          </div>

          {/* Currency Rates Grid */}
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <Label className="text-lg font-semibold">Exchange Rates</Label>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={resetToDefaults}
              >
                Reset to Defaults
              </Button>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {CURRENCIES.map((currency) => (
                <div key={currency.code} className="space-y-2">
                  <Label htmlFor={currency.code} className="font-medium">
                    {currency.code} - {currency.name}
                  </Label>
                  <div className="relative">
                    <DollarSign className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                    <Input
                      id={currency.code}
                      type="number"
                      step="0.0001"
                      min="0"
                      placeholder="0.0000"
                      value={rates[currency.code] || ''}
                      onChange={(e) => handleRateChange(currency.code, e.target.value)}
                      className="pl-10"
                    />
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Submit Button */}
          <div className="flex justify-end space-x-3">
            <Button
              type="submit"
              disabled={createBulkRates.isPending}
              className="bg-orange-500 hover:bg-orange-600"
            >
              {createBulkRates.isPending ? 'Creating Rates...' : 'Create All Rates'}
            </Button>
          </div>
        </form>
      </CardContent>
    </Card>
  );
}